import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { PiArrowLeft } from 'react-icons/pi'
import { ContactCompany } from 'models/companies'
import { EmailMessage, EmailThread } from 'models/emailIntegration'
import { Modal } from 'components/UI/Modal'
import useApiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { EmailsModalHeader, ModalView } from './EmailsModalHeader'
import { EmailInboxView } from './EmailInboxView'
import { EmailDetailView, EmailOnlyDetailView } from './EmailDetailView'
import { ComposePanel, ComposeMode, SendPayload } from './ComposePanel'
import { InboxTab } from './InboxTabs'
import { PAGE_SIZE } from './EmailInboxView'
import { handleEmailError } from './handleEmailError'
import { LegacyEmailsModal } from './LegacyEmailsModal'
import type { LockedFilters } from './InboxFilterChips'

interface BaseEmailsModalProps {
  show: boolean
  onHide: () => void
  forDoorProxyId?: number
  companyName?: string
  /** Filters that are always applied and cannot be removed by the user. */
  lockedFilters?: LockedFilters
}

interface EmailsListModalProps extends BaseEmailsModalProps {
  /** When true, shows only the email view without list navigation */
  emailOnlyMode?: false
  contactCompany: ContactCompany
  /** If provided, opens directly to this email */
  initialEmail?: EmailMessage
}

interface EmailOnlyModalProps extends BaseEmailsModalProps {
  /** When true, shows only the email view without list navigation */
  emailOnlyMode: true
  contactCompany?: never
  /** The email to display (required in emailOnlyMode) */
  initialEmail: EmailMessage
}

type EmailsModalProps = EmailsListModalProps | EmailOnlyModalProps

export function EmailsModal(props: EmailsModalProps) {
  const featureFlag = useFeatureFlagService()

  if (!featureFlag.enableEmailInboxView) {
    return <LegacyEmailsModal {...props} />
  }

  return <InboxEmailsModal {...props} />
}

/**
 * Email modal supporting three modes: thread-based inbox view with filtering/pagination,
 * email detail view, or a compose view. Handles thread selection, navigation,
 * and state reset on close.
 */
function InboxEmailsModal(props: EmailsModalProps) {
  const {
    show,
    onHide,
    forDoorProxyId,
    companyName,
    initialEmail,
    emailOnlyMode,
    lockedFilters,
  } = props

  const contactCompany = emailOnlyMode ? undefined : props.contactCompany

  const [view, setView] = useState<ModalView>(
    emailOnlyMode || initialEmail ? 'email' : 'list'
  )
  const [selectedTab, setSelectedTab] = useState<InboxTab>('my_emails')
  const [selectedThread, setSelectedThread] = useState<
    EmailThread | undefined
  >()
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | undefined>(
    initialEmail
  )

  // Keep track of the current threads list for prev/next navigation
  const threadsRef = useRef<EmailThread[]>([])
  const [threadIndex, setThreadIndex] = useState(0)
  const [listPage, setListPage] = useState(1)
  const [listTotal, setListTotal] = useState(0)
  const [initialComposeMode, setInitialComposeMode] = useState<
    ComposeMode | undefined
  >()

  // Handle initial email navigation
  useEffect(() => {
    if (show && initialEmail) {
      setSelectedEmail(initialEmail)
      setView('email')
    }
  }, [show, initialEmail])

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setView(emailOnlyMode ? 'email' : 'list')
      setSelectedEmail(emailOnlyMode ? initialEmail : undefined)
      setSelectedThread(undefined)
      setSelectedTab('my_emails')
      setThreadIndex(0)
    }
  }, [show, emailOnlyMode, initialEmail])

  const handleThreadClick = useCallback(
    (
      thread: EmailThread,
      index: number,
      threads: EmailThread[],
      page: number,
      total: number,
      composeMode?: ComposeMode
    ) => {
      setSelectedThread(thread)
      setThreadIndex(index)
      threadsRef.current = threads
      setListPage(page)
      setListTotal(total)
      setInitialComposeMode(composeMode)
      setView('email')
    },
    []
  )

  const handleNavigateThread = useCallback(
    (delta: number) => {
      const newIndex = threadIndex + delta
      const threads = threadsRef.current
      if (newIndex >= 0 && newIndex < threads.length) {
        setSelectedThread(threads[newIndex])
        setThreadIndex(newIndex)
      }
    },
    [threadIndex]
  )

  const handleBack = useCallback(() => {
    setView('list')
    setSelectedThread(undefined)
    setSelectedEmail(undefined)
    setInitialComposeMode(undefined)
  }, [])

  const handleCompose = useCallback(() => {
    setView('compose')
  }, [])

  const api = useApiService()

  // Standalone compose send (no thread context for optimistic UI)
  const composeMutation = useMutation({
    mutationFn: ({ formData, fromId }: SendPayload) =>
      api.emailIntegration.sendEmailMessage(fromId, formData),
    onSuccess: () => toast.success('Email sent successfully'),
    onError: async (err) => handleEmailError(err, api.emailIntegration),
  })

  const handleComposeSend = useCallback(
    (payload: SendPayload) => {
      setView('list')
      composeMutation.mutate(payload)
    },
    [composeMutation]
  )

  const handleComposeDiscard = useCallback(() => {
    setView('list')
  }, [])

  const displayName = companyName || contactCompany?.name
  const pageStart = (listPage - 1) * PAGE_SIZE + 1

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      enforceFocus={false}
      backdrop="static"
      className="[&_.modal-content]:bg-surface-alt"
    >
      <button
        type="button"
        className="btn-close absolute top-5 right-[30px] z-10"
        aria-label="Close"
        onClick={onHide}
      />
      <EmailsModalHeader
        view={view}
        companyName={displayName}
        emailSubject={selectedEmail?.subject}
        emailOnlyMode={emailOnlyMode}
      />

      <Modal.Body className="px-[24px] pb-[24px] pt-0">
        <div
          style={{ height: '70vh', display: 'flex', flexDirection: 'column' }}
        >
          {view === 'compose' ? (
            <ComposeEmailView
              companyName={displayName}
              onBack={handleComposeDiscard}
              onSend={handleComposeSend}
              onDiscard={handleComposeDiscard}
            />
          ) : view === 'list' && contactCompany ? (
            <EmailInboxView
              contactCompanyId={contactCompany.id}
              forDoorProxyId={forDoorProxyId}
              selectedTab={selectedTab}
              onTabChange={setSelectedTab}
              onThreadClick={handleThreadClick}
              onCompose={handleCompose}
              lockedFilters={lockedFilters}
            />
          ) : emailOnlyMode && selectedEmail ? (
            <EmailOnlyDetailView email={selectedEmail} />
          ) : selectedThread ? (
            <EmailDetailView
              thread={selectedThread}
              threadIndex={threadIndex}
              totalThreads={listTotal}
              pageSize={PAGE_SIZE}
              pageStart={pageStart}
              contactCompanyId={contactCompany!.id}
              contactCompany={contactCompany}
              companyName={displayName}
              initialComposeMode={initialComposeMode}
              onBack={handleBack}
              onNavigateThread={handleNavigateThread}
            />
          ) : selectedEmail ? (
            <EmailOnlyDetailView email={selectedEmail} />
          ) : null}
        </div>
      </Modal.Body>
    </Modal>
  )
}

// ─── Standalone compose view ─────────────────────────────────────────────────

interface ComposeEmailViewProps {
  companyName?: string
  onBack: () => void
  onSend: (payload: SendPayload) => void
  onDiscard: () => void
}

/** Full-height compose view matching the email detail card layout with back button. */
function ComposeEmailView({
  companyName,
  onBack,
  onSend,
  onDiscard,
}: ComposeEmailViewProps) {
  return (
    <div className="h-full flex flex-col border border-line-subtle rounded-lg overflow-hidden bg-surface">
      {/* Action bar with back button */}
      <div className="px-4 py-2 border-b border-line-subtle">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 h-8 rounded hover:bg-surface-alt px-1.5 text-sm font-medium text-fg transition-colors"
        >
          <PiArrowLeft className="size-[22px]" />
          <span>Back</span>
        </button>
      </div>

      {/* Compose panel fills remaining space */}
      <ComposePanel
        mode="compose"
        onSend={onSend}
        onDiscard={onDiscard}
        companyName={companyName}
        standalone
      />
    </div>
  )
}
